import { pdsUtils } from '../../pds-utils';
import { pdsLogger } from '../../pds-logger';
import { Offset } from '../../interfaces/offset';

let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('parsed-html-content'), 'document-section.ts');

const minLeft = 0;
const minTop = 0;
const buttonLength = 32;

export class HtmlContentSection {
  bounds: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  button: Offset;
  nodes: Array<HTMLElement | Node> = [];
  focused = false;
  selected = false;

  // the reasons can be used to debug why this section was created
  constructor(
    private container: HTMLElement,
    public maxRight: number,
    public maxBottom: number,
    public reasons: Array<string>) {
    log(`A new section was created for the following reason${reasons.length > 1 ? 's' : ''}: ${reasons.join(', ')}`);
  }

  // add this element to the list of selected nodes
  addNode(node: HTMLElement | Node): void {
    this.nodes.push(node);
    this.updateBounds(node);
  }

  // gets the text from each node in this section
  getText(): Array<string> {
    return this.nodes
      .map((node: HTMLElement | Node) => node instanceof HTMLElement ? node.innerText : node.textContent)
      // exclude null and empty
      .filter((text: string | null) => !!text)
      .map((text: string) => text.trim());
  }

  // check if the new element is already included in this section
  includesElement(element: HTMLElement): boolean {
    return this.nodes.some((node: HTMLElement | Node) => node === element);
  }

  // reset the bounds object and recreate it from the current list of elements
  resetBounds(newMaxRight?: number, newMaxBottom?: number): void {
    this.maxRight = newMaxRight || this.maxRight;
    this.maxBottom = newMaxBottom || this.maxBottom;

    delete this.bounds;
    for (let node of this.nodes) {
      this.updateBounds(node);
    }
  }

  // see if the new element is outside or partially outside the current bounds. If it is, extend the bounds to encompass the new element
  updateBounds(node: HTMLElement | Node): void {
    // we need to use different methods to get the bounding box for text nodes vs full elements
    let position = node instanceof HTMLElement
      ? pdsUtils.getBoundingBoxOffsetFromContainer(this.container, node, false)
      : pdsUtils.getTextNodeBoundingBoxOffsetFromContainer(this.container, node);

    let left = position.left;
    let top = position.top;
    let width = position.right - position.left;
    let height = position.bottom - position.top;

    // if the bounds have not been set yet, initialize them to the new node's values
    if (this.bounds == null) {
      this.bounds = { left, top, width, height };
    } else {
      // otherwise expand the current bounds to include the new node
      this.bounds.left = Math.min(this.bounds.left, left);
      this.bounds.top = Math.min(this.bounds.top, top);

      // re-calculate the width/height from the new right/bottom points and the expanded left/top points
      width = position.right - this.bounds.left;
      height = position.bottom - this.bounds.top;

      // expand the width/height to include the new node
      this.bounds.width = Math.max(this.bounds.width, width);
      this.bounds.height = Math.max(this.bounds.height, height);
    }

    // make sure the left bound is within the max left bound
    if (this.bounds.left < minLeft) {
      this.bounds.width -= minLeft - this.bounds.left;
      this.bounds.left = minLeft;
    }

    // make sure the top bound is within the max top bound
    if (this.bounds.top < minTop) {
      this.bounds.height -= minTop - this.bounds.top;
      this.bounds.top = minTop;
    }

    // make sure the width won't cross the max right bound
    if (this.bounds.left + this.bounds.width > this.maxRight) {
      this.bounds.width = this.maxRight - this.bounds.left;
    }

    // make sure the height won't cross the max bottom bound
    if (this.bounds.top + this.bounds.height > this.maxBottom) {
      this.bounds.height = this.maxBottom - this.bounds.top;
    }

    this.updateButtonPosition();
  }

  updateButtonPosition(): void {
    if (this.bounds == null) {
      return;
    }

    let left = this.bounds.left + this.bounds.width / 2;
    let top = this.bounds.top + this.bounds.height / 2;

    // 16 is half of the 32px used for the height and width of round buttons
    left -= buttonLength / 2;
    top -= buttonLength / 2;

    this.button = { left, top };
  }
}
